/**
 * Cross Sell component.
 *
 * @module app/components/crossSell/CrossSell
 */
import CrossSellAnalyticsTracking from '../crossSell/CrossSellAnalyticsTracking';
import CrossSellCommon from '../crossSell/CrossSellCommon';
import CrossSellPerformanceTracking from '../crossSell/CrossSellPerformanceTracking';
import DeviceDetector from '../DeviceDetector';
import docCookies from 'doc-cookies';
import GameLauncher from '../crossSell/games/Launcher';
import GamesBanners from '../crossSell/games/GamesBanners';
import jQuery from 'jquery';
import Login from '../Login';
import MessageBusTopics from '../../../common/MessageBusTopics';
import Translator from '../Translator';
import { ContentModifierPlugin } from '../contentModifier/Plugin';
import { doGameClickTracking } from './analytics/gameClick';
import { doBannerClickTracking } from './analytics/bannerClick';

/**
 * Minimum version of native apps to display icons in footer
 *
 * @type {Object}
 *
 * @private
 */

var minimumAppVersions = {
    roulette: {
        ios: 7.8,
        android: 0,
    },
};

/**
 * Whether to open game in new tab or current
 * @type {string}
 */
let gameOpenTarget = '_blank';

/**
 * Listens for an update to the game target so we can test opening in same window
 */
const listenForGameTargetUpdate = () => {
    window.WH.messageBus.subscribe('crossSell.updateGameTarget', (topic, target) => {
        gameOpenTarget = target;
    });
};

/**
 * Name of the cookie, that will be used to detect whether user (any user)
 * has ever logged in with the current browser.
 *
 * @type {String}
 *
 * @private
 */
var googleCookieName = '__utmv';

/**
 * Name of the username cookie, where username should be stored and persisted
 * after successful login/registration.
 *
 * @type {String}
 *
 * @private
 */
var usernameCookie = 'username_cookie';

/**
 * The jQuery selector that points to banners' top-level container element.
 *
 * @type {Object}
 *
 * @private
 */
var bannersContainerSelector = null;

/**
 * Finds the banners' top level container element and stores it in a variable.
 *
 * @returns {void}
 *
 * @private
 */
var setBannersContainer = function setBannersContainer() {
    bannersContainerSelector = jQuery('#contentarea').find('div.column__secondary')
        .add('#main-off-canvas aside.off-canvas__right');
};

/**
 * Removes the 'displayNone' class from the banners that need to be
 * shown to the particular client.
 *
 * @returns {void}
 *
 * @private
 */
var selectBannersToDisplay = function selectBannersToDisplay() {

    bannersContainerSelector.find('article.cpt-banner-cross-sell')
        .each(enableAllowedBanner);
};

/**
 * Checks whether roulette should be shown
 *
 * @returns {boolean}
 *
 * @private
 */
var shouldShowRoulette = function shouldShowRoulette() {

    if (!DeviceDetector.isNative()) {

        return true;
    }

    var appVer = parseFloat(DeviceDetector.getAppVersion());

    return (
        (DeviceDetector.isNativeiOS() && appVer >= minimumAppVersions.roulette.ios) ||
        (DeviceDetector.isNativeAndroid() && appVer >= minimumAppVersions.roulette.android)
    );
};

/**
 * Checks whether the games lobby should be shown
 * @return {boolean}
 */
var shouldShowGamesLobby = function shouldShowGamesLobby() {
    return !DeviceDetector.isDesktop();
};

/**
 * Removes the 'displayNone' class from the banners that need to be
 * shown to the particular client.
 *
 * @returns {void}
 *
 * @private
 */
var setGamesButton = function setGamesButton() {

    if (shouldShowRoulette()) {

        jQuery('#toolbar').find('#toolbarRouleteContainer')
            .removeClass('wh-hide');
    }

    if (shouldShowGamesLobby()) {

        jQuery('#toolbar').find('#gamesLobbyContainer')
            .removeClass('wh-hide');
    }
};

/**
 * Make the allowed banner visible by removing the 'displayNone' class from
 * the jquery banner selector.
 *
 * @returns {void}
 *
 * @private
 */
var enableAllowedBanner = function enableAllowedBanner() {

    var selector = jQuery(this);

    if (isBannerForCurrentPlatform(selector) &&
        shouldBeShownIfIsSignupBanner(selector)) {
        selector.removeClass('displayNone');
    }
};

/**
 * Prevents showing the banner, if the banner is of sign-up type, and:
 *     - user is already logged in; or
 *     - (googleCookieName cookie is available, and contains string
 *       'AccNum' in its value (stating someone has ever logged in
 *       with the current browser)) OR usernameCookie is present
 *       and has non-empty value.
 *
 * @param   {Object}   selector  jquery selector for the banner element.
 *
 * @returns {Boolean}  false, if banner is of sign-up type AND player
 *                     is already logged in or someone has ever logged
 *                     in from the browser.
 *
 * @private
 */
var shouldBeShownIfIsSignupBanner = function shouldBeShownIfIsSignupBanner(selector) {

    return !(isSignupBanner(selector) &&
        (Login.isLoggedIn() || hasEverLoggedIn()));
};

/**
 * Verify whether the banner is of sign-up type, ie meant to be shown
 * to the non-logged in users only.
 *
 * @param   {Object}  selector  jquery selector for the banner element.
 *
 * @returns {Boolean} true if banner is of sign-up type.
 *
 * @private
 */
var isSignupBanner = function isSignupBanner(selector) {

    return selector.attr('bannertype') === 'Segmented';
};

/**
 * Finds cookie contents for given cookie name.
 *
 * @param   {String}   cookie  name of the cookie which value to find.
 *
 * @returns {String}  value for the given cookie name, or empty string,
 *                    if no cookie with given name was found.
 *
 * @private
 */
var getCookieValue = function getCookieValue(cookie) {

    return docCookies.getItem(cookie) || '';
};

/**
 * Checks whether the the browser has ever been used for registration
 * or logging in by analyzing cookies.
 *
 * @returns {Boolean}  true, if this browser has already been used for
 *                     registering/logging in.
 *
 * @private
 */
var hasEverLoggedIn = function hasEverLoggedIn() {

    return hasGoogleCookie() ||
        getCookieValue(usernameCookie);
};

/**
 * Checks whether googleCookieName cookie value contains
 * 'AccNum' string; this essentially confirms, that some user has already
 * logged in with the current browser at some point in time.
 *
 * @returns {Boolean}  true, if googleCookieName is present
 *                     AND its value contains 'AccNum' string.
 *
 * @private
 */
var hasGoogleCookie = function hasGoogleCookie() {

    var cookieValue = getCookieValue(
        googleCookieName);

    return cookieValue.indexOf('AccNum') > -1;
};

/**
 * Verify if the banner should be shown to this particular client platform.
 * Note that native app is handled as a separate case.
 *
 * @param   {Object}  selector  jquery selector for the banner element.
 *
 * @returns {Boolean} true if banner is eligible to be shown to the
 *                    client, platform-wise (ie the element's 'platforms'
 *                    attribute contains the current client platform).
 *
 * @private
 */
var isBannerForCurrentPlatform = function isBannerForCurrentPlatform(selector) {

    var platformsToShowBannerTo = selector.attr('platforms');

    if (DeviceDetector.isNative()) {
        return (platformsToShowBannerTo.indexOf('native') > -1);
    } else {
        return (platformsToShowBannerTo.indexOf('web') > -1);
    }
};

/**
 * Name of the override cookie.
 *
 * @type {String}
 * @private
 */
var overrideCookieName = 'wh_traf_bet';

/**
 * Value of the override cookie.
 *
 * @type {String}
 * @private
 */
var overrideCookieValue = '';

/**
 * Read user cookies.
 *
 * @returns {void}
 * @private
 */
var getCookies = function getCookies() {

    if (docCookies.hasItem(overrideCookieName)) {

        overrideCookieValue = docCookies.getItem(overrideCookieName);
    }
};

/**
 * Determine whether or not the override cookie has been set.
 *
 * @returns {Boolean}
 * @private
 */
var hasCookieOverride = function hasCookieOverride() {

    return (overrideCookieValue === 'Yes');
};

/**
 * Function to prevent default behaviour of event
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var preventDefaultBehaviour = function preventDefaultBehaviour(event) {

    event.preventDefault();
    event.stopPropagation();
};

/**
 * Navigates to url defined in event.data.url in the same window.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToSameWindow = function navigateToSameWindow(event) {

    window.location.assign(event.data.url);
    preventDefaultBehaviour(event);
};

/**
 * Navigates to url defined in event.data.url in a new tab.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToNewTab = function navigateToNewTab(event) {

    window.open(event.data.url, '_blank');
    preventDefaultBehaviour(event);
};

/**
 * Navigates to url defined in event.data.url in a new window,
 * if possible.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToNewWindow = function navigateToNewWindow(event) {

    // TODO: define dimensions/other attrs to force a new window?
    //window.open(event.data.url, '_blank', features);
    window.open(event.data.url, '_blank');
    preventDefaultBehaviour(event);
};

/**
 * Navigates to url defined in event.data.url in an overlay opened
 * by the magnificpopup plugin.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToOverlay = function navigateToOverlay(event) {

    jQuery.magnificPopup.open({
        items: {
            src: event.data.url,
        },
        type: 'iframe',
    });

    preventDefaultBehaviour(event);
};

/**
 * Do nothing, as in don't navigate/redirect anywhere.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToVoid = function navigateToVoid(event) {

    preventDefaultBehaviour(event);
};

/**
 * Navigates to url defined in event.data.url in a defined pop-up window.
 * This is intended to be used exclusively with the gaming banners.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToPopUpWindow = function navigateToPopUpWindow(event) {

    var features = 'toolbar=no,width=799,height=600,resizable=no,menubar=no,status=no,location=no';

    if (DeviceDetector.isMobile() || DeviceDetector.isTablet()) {
        navigateToNewTab(event);
    } else {
        window.open(event.data.url, '_blank', features);
    }

    preventDefaultBehaviour(event);
};

/**
 * Delegates navigation to signup page by locating and clicking
 * on the 'JOIN' button.
 *
 * @param   {Object} event  jquery normalized event based on the browser
 *                   native event object.
 *
 * @returns {void}
 *
 * @private
 */
var navigateToSignupOverlay = function navigateToSignupOverlay(event) {

    window.WH.messageBus.publish(MessageBusTopics.MY_ACCOUNT_OVERLAY_SHOW_REGISTRATION);
    preventDefaultBehaviour(event);
};

/**
 * Maps different targetUrlTypes to their respective
 *
 * @type {Object}
 *
 * @private
 */
var navigationTypesToClickHandlers = {
    _self: function (event) {
        navigateToSameWindow(event);
    }, _blank: function (event) {
        navigateToNewTab(event);
    }, new: function (event) {
        navigateToNewWindow(event);
    }, overlay: function (event) {
        navigateToOverlay(event);
    }, popup: function (event) {
        navigateToPopUpWindow(event);
    }, segmented: function (event) {
        navigateToSignupOverlay(event);
    }, none: function (event) {
        navigateToVoid(event);
    },
};

/** * Binds event handlers to cross sell elements.
 *
 * @returns {void}
 *
 * @private
 */
var bindEvents = function bindEvents() {

    window.WH.messageBus.subscribe(MessageBusTopics.DEFERRED_LOAD_MAGNIFIC_POPUP, function () {
        jQuery('body').off('click.crossSell')
            .on('click.crossSell', 'a.localnavigation__button-dropdown--xsell,' +
                'a.contextual-nav__game-launcher,' +
                '.x-sell-btn-container > a.localnavigation__button-dropdown,' +
                '#contentarea div.home-banners a,' +
                '#toolbar .roulette,' +
                '#desktop-sidebar-crosssell .cross-sell__item:not(#more-games),' +
                '#top-games .topgames-game-icon--click-handler a,' +
                '#top-games .xsell-notification--game .xsell-notification__icon a,' +
                '#top-games .cpt-banner-enhanced--gameLauncher a.non-route',
            crossSellClickHandler)
            .on('click.crossSell', '#top-games .topgames-header__link,' +
                '#top-games .topgames-header__view-more',
            crossSellSectionHeaderClickHandler);

        bannersContainerSelector.find('article.cpt-banner-cross-sell')
            .not('.displayNone')
            .each(bindIndividualBannerNavigation);

        jQuery('#more-games').off('click.crossSell').on('click', launchMoreGames);

    });
};

/**
 * Launches More games section in Overlay.
 *
 * @param {Object} e event object
 *
 * @returns {void}
 *
 * @private
 */
var launchMoreGames = function launchMoreGames(e) {
    var selector = jQuery(this);
    var url = selector.data('url');

    window.location = url;
    e.preventDefault();
};

/**
 * Binds clickhandler to banner depending on which clickhandler is
 * required.
 *
 * @returns {void}
 *
 * @private
 */
var bindIndividualBannerNavigation = function bindIndividualBannerNavigation() {

    var selector = jQuery(this);
    var urlData = resolveUrlData(selector);
    var clickHandler = navigationTypesToClickHandlers[urlData.urlTargetType];
    // sanitize:
    if (!clickHandler) {
        clickHandler = navigateToVoid;
    }

    selector.off('click').on('click', urlData, (event) => {
        doBannerClickTracking(urlData.title);
        clickHandler(event);
    });
};

/**
 * Compiles the object containing url info based on the platform client
 * is using.
 *
 * @param   {Object} selector  jquery selector for the banner element.
 *
 * @returns {Object} an object containing data of the actual url and the
 *                   target url type (as in how the url should be opened).
 *
 * @private
 */
var resolveUrlData = function resolveUrlData(selector) {

    var resolveUrlDataBasedOnDevice = function (selector) {

        var platform = (DeviceDetector.isMobile() || DeviceDetector.isTablet()) ? 'mobile' : 'desktop';
        var urlSelector = selector.find('a.cpt-banner__copy');

        return {
            url: urlSelector.attr('url-' + platform),
            urlTargetType: urlSelector.attr('target-type-' + platform),
            title: urlSelector.attr('title'),
        };
    };

    var urlData = resolveUrlDataBasedOnDevice(selector);

    // sanitize:
    if (!urlData.urlTargetType) {
        urlData.urlTargetType = 'none';
    }

    return urlData;
};

/**
 * Launches a game.
 *
 * @param {Object} game game data object
 *
 * @returns {void}
 *
 * @private
 */
var launchGame = function launchGame(game) {

    if (canLaunchEmbeddedGame(game)) {

        game.gameType = 'embedded';

        launchEmbeddedGame(game);
    } else {
        launchNonEmbeddedGame(game);
    }
};


/**
 * Handles clicks on individual cross sell elements.
 *
 * @param {Object} e event object
 *
 * @returns {void}
 *
 * @private
 */
var crossSellClickHandler = function crossSellClickHandler(e) {

    var clickedGame = jQuery(this);

    var game = {
        desktopUrl: clickedGame.attr('desktop-url'),
        mobileUrl: clickedGame.attr('mobile-url'),
        embeddedUrl: clickedGame.attr('embedded-url'),

        location: clickedGame.attr('data-location'),
        position: clickedGame.attr('data-position'),
        name: clickedGame.attr('game-name'),
        gameType: null,
        gameName: clickedGame.attr('data-game-name'),
        gameCode: clickedGame.attr('data-game-code'),

        funnel: CrossSellCommon.getTrackingFunnel(),
    };

    doGameClickTracking({
        EventLabel: game.name,
        GameCode: game.gameCode,
        GameList: game.location,
        GameListPosition: game.position,
        GameName: game.gameName,
    });

    launchGame(game);
    e.preventDefault();
};


/**
 * Handles clicks on section header cross sell element.
 *
 * @param {Object} e event object
 *
 * @returns {void}
 *
 * @private
 */
var crossSellSectionHeaderClickHandler = function crossSellSectionHeaderClickHandler(e) {

    var clickedSection = jQuery(this);

    var section = {
        sectionUrl: clickedSection.attr('section-url'),
        sectionName: clickedSection.attr('title'),
        sectionIndex: clickedSection.attr('section-index'),
        location: 'top_games',
    };

    launchSectionHeader(section);
    e.preventDefault();

};


/**
 * Determine whether embedded games are enabled by config.
 *
 * @returns {Boolean}
 * @private
 */
var isEmbeddedEnabledByConfig = function isEmbeddedEnabledByConfig() {

    return (
        window.whStatic &&
        window.whStatic.config &&
        window.whStatic.config.crossSell &&
        window.whStatic.config.crossSell.embeddedEnabled
    );
};

/**
 * Determine whether or not embedded games can be launched.
 *
 * @returns {Boolean}
 * @private
 */
var canLaunchEmbeddedGames = function canLaunchEmbeddedGames() {

    return (
        hasCookieOverride() ||
        isEmbeddedEnabled()
    );
};

/**
 * Determiner whether or not embedded is enabled.
 *
 * @returns {Boolean}
 * @private
 */
var isEmbeddedEnabled = function isEmbeddedEnabled() {

    return (
        isEmbeddedEnabledByConfig() &&
        (DeviceDetector.isMobile() || DeviceDetector.isNative())
    );
};

/**
 * Determine whether a game can be launched.
 *
 * @param {Object} game Game spec.
 * @returns {Boolean}
 * @private
 */
var canLaunchEmbeddedGame = function canLaunchEmbeddedGame(game) {

    return (
        canLaunchEmbeddedGames() &&
        game.embeddedUrl
    );
};

/**
 * Launch an embedded game.
 *
 * @param {Object} game Game spec.
 * @returns {void}
 * @private
 */
var launchEmbeddedGame = function launchEmbeddedGame(game) {

    if (window.WH.sportsbook.isLoggedIn()) {

        game.embeddedUrl = CrossSellPerformanceTracking.appendURLParams(game.embeddedUrl, game);

        CrossSellAnalyticsTracking.publishGameLaunchEvent(game);
        GameLauncher.launch(game.embeddedUrl);

    } else {
        forceLogin(game);
    }
};

/**
 * Force the user to log in.
 *
 * @param {Object} game Game definition.
 *
 * @returns {void}
 * @private
 */
var forceLogin = function forceLogin(game) {

    if (DeviceDetector.isNative()) {

        nativeUnloggedFlow(game);

    } else {

        window.location.hash = '';
        forceNonNativeLogin();
    }
};

/**
 * Trigger a native scheme.
 *
 * @param {String} scheme Url to open.
 * @returns {void}
 * @private
 */
var triggerNativeScheme = function triggerNativeScheme(scheme) {

    var frame = document.createElement('iframe');
    frame.src = scheme;
    document.body.appendChild(frame);

    window.setTimeout(function () {
        document.body.removeChild(frame);
    }, 2000);
};

/**
 * The continuation to follow when user tries to launch an embedded game in native
 * when not logged in.
 *
 * @returns {void}
 * @private
 */
var nativeUnloggedFlow = function nativeUnloggedFlow() {

    triggerNativeScheme('nw-showlogin://');
};

/**
 * Force the user to log in on a non-native device.
 *
 * @returns {void}
 * @private
 */
var forceNonNativeLogin = function forceNonNativeLogin() {

    // TODO: replace alertReference to alert when the native implementation will be available
    var alert = window.alertReference || window.alert;
    alert(Translator.translate('components.crossSell.loginPhrase'));
    window.WH.messageBus.publish(MessageBusTopics.SHOW_LOGIN);
};

/**
 * Launch a non-embedded game.
 *
 * @param {Object} game Game spec.
 * @returns {void}
 * @private
 */
var launchNonEmbeddedGame = function launchNonEmbeddedGame(game) {

    if (DeviceDetector.isMobile() || DeviceDetector.isTablet()) {

        game.gameType = 'mobile';
        launchMobileGame(game);
    } else {

        game.gameType = 'desktop';
        launchDesktopGame(
            game,
            'toolbar=no,width=1200,height=900,resizable=no,menubar=no,status=no,location=no'
        );
    }
};

/**
 * Launches a game on mobile via loading its URL.
 *
 * @param game Game object with the URLs of the game and data
 *
 * @returns {void}
 *
 * @private
 */
var launchMobileGame = function launchMobileGame(game) {

    game.gameType = 'mobile';

    game.mobileUrl = CrossSellPerformanceTracking.appendURLParams(game.mobileUrl, game);

    CrossSellAnalyticsTracking.publishGameLaunchEvent(game);
    window.open(game.mobileUrl, gameOpenTarget);
};

/**
 * Launches a section header in a new tab
 *
 * @param section Section object with the URLs of the game and data
 *
 * @returns {void}
 *
 * @private
 */
var launchSectionHeader = function launchMobileGame(section) {

    CrossSellAnalyticsTracking.publishSectionViewMoreEvent(section);
    window.open(section.sectionUrl, '_blank');
};

/**
 * Launches a game on desktop via creating a pop-up window.
 *
 * @param gameUrl the URL of the game.
 * @param windowFeatures the additional features of the pop-up window
 *
 * @returns {void}
 *
 * @private
 */
var launchDesktopGame = function launchDesktopGame(game, windowFeatures) {

    game.desktopUrl = CrossSellPerformanceTracking.appendURLParams(game.desktopUrl, game);

    CrossSellAnalyticsTracking.publishGameLaunchEvent(game);

    window.open(
        game.desktopUrl,
        gameOpenTarget,
        windowFeatures
    );
};

/**
 *
 * @param topic
 * @param data
 */
var launchGameFromMessage = function launchGameFromMessage(topic, data) {

    var game = {
        embeddedUrl: data.embeddedUrl,
        desktopUrl: data.desktopUrl,
        mobileUrl: data.mobileUrl,
        position: data.position,
        name: data.name,
        location: data.location,
        funnel: CrossSellCommon.getTrackingFunnel(),
    };

    launchGame(game);
};

/**
 * Launch an embedded game from embeddedGame.show.
 *
 * @param {String} topic Message bus topic.
 * @param {Object} data Message payload. url => URL of game, game => short name of game.
 *
 * @returns {void}
 * @private
 */
var launchEmbeddedGameFromMessage = function launchEmbeddedGameFromMessage(topic, data) {

    var game = {
        embeddedUrl: data.url,
        desktopUrl: null,
        mobileUrl: null,
        position: null,
        name: data.game,
        location: 'sidebar', //@TODO - dont really think this should be hard coded here
        gameType: 'embedded',
        funnel: CrossSellCommon.getTrackingFunnel(),
    };

    if (canLaunchEmbeddedGame(game)) {
        launchEmbeddedGame(game);
    }
};

/**
 * Bind to message bus topics.
 *
 * @returns {void}
 * @private
 */
var addListeners = function addListeners() {

    window.WH.messageBus.subscribe(MessageBusTopics.CROSS_SELL_LAUNCH_GAME, launchGameFromMessage);
    window.WH.messageBus.subscribe(MessageBusTopics.EMBEDDED_GAME_SHOW, launchEmbeddedGameFromMessage);
    window.WH.messageBus.subscribe(MessageBusTopics.CROSS_SELL_NAVIGATE, (name, { url }) => {
        window.WH.messageBus.publish(MessageBusTopics.DO_INTERNAL_ROUTE, { url });
    });
};

/**
 * Contains functions exposed by this component as an API.
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Perform initialisation.
     *
     * @returns {void}
     *
     * @public
     */
    init: function init() {

        getCookies();
        setBannersContainer();
        selectBannersToDisplay();
        setGamesButton();

        bindEvents();
        addListeners();

        if (window.WH && window.WH.sportsbook && window.WH.sportsbook.crossBannerMode === 'old') {
            GamesBanners.init();
        }

        this.contentModifierPlugin = new ContentModifierPlugin('crossSell');
        listenForGameTargetUpdate();
    },
};

export default api;
